import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from './Navbar';
import Footer from './Footer';
import './DownloadFiles.css';

const API_BASE_URL = "https://api.rescons.in";

const DownloadFiles = () => {
  const [step, setStep] = useState(1); // 1: email, 2: OTP, 3: download
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'
  const [files, setFiles] = useState([]);
  const [otpTimer, setOtpTimer] = useState(0);
  const [verifiedEmail, setVerifiedEmail] = useState('');

  useEffect(() => {
    fetchFiles();
  }, []);

  useEffect(() => {
    let interval = null;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer(otpTimer - 1);
      }, 1000);
    } else if (otpTimer === 0 && interval) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  const fetchFiles = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/downloads/files`);
      setFiles(response.data.files || []);
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  };

  const showMessage = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      // Verify email is in allowed list
      const verifyResponse = await axios.post(`${API_BASE_URL}/api/downloads/verify-email`, {
        email: email.trim().toLowerCase()
      });

      if (verifyResponse.data.message === 'Email verified') {
        setVerifiedEmail(email.trim().toLowerCase());
        // Send OTP
        await handleSendOTP();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to verify email. Please try again.';
      showMessage(errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSendOTP = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/downloads/send-otp`, {
        email: email.trim().toLowerCase()
      });

      if (response.data.message === 'OTP sent successfully to your email') {
        setStep(2);
        setOtpTimer(600); // 10 minutes
        showMessage('OTP sent to your email. Please check your inbox.', 'success');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to send OTP. Please try again.';
      showMessage(errorMsg, 'error');
    }
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const response = await axios.post(`${API_BASE_URL}/api/downloads/verify-otp`, {
        email: email.trim().toLowerCase(),
        code: otp
      });

      if (response.data.message === 'OTP verified successfully') {
        setStep(3);
        showMessage('Verification successful! You can now download the files.', 'success');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Invalid OTP. Please try again.';
      showMessage(errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (fileType, fileName) => {
    try {
      // Use the direct file serving endpoint
      const fileUrl = `${API_BASE_URL}/api/downloads/file/${fileType}?email=${encodeURIComponent(verifiedEmail)}`;
      
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showMessage('Download started!', 'success');
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to download file. Please try again.';
      showMessage(errorMsg, 'error');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const abstractBooklet = files.find(f => f.fileType === 'abstract-booklet');
  const papersBook = files.find(f => f.fileType === 'papers-book');

  return (
    <>
      <Navbar />
      <div className="download-files-container">
        <div className="download-files-content">
          <h1>Download Conference Materials</h1>
          <p className="download-subtitle">
            Access the Conference Proceedings by verifying your email address.
          </p>

          {message && (
            <div className={`download-message ${messageType}`}>
              {message}
            </div>
          )}

          {step === 1 && (
            <div className="download-step">
              <h2>Step 1: Enter Your Email</h2>
              <form onSubmit={handleEmailSubmit} className="download-form">
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your registered email"
                    required
                    disabled={loading}
                  />
                </div>
                <button type="submit" className="download-btn" disabled={loading}>
                  {loading ? 'Verifying...' : 'Verify Email & Send OTP'}
                </button>
              </form>
            </div>
          )}

          {step === 2 && (
            <div className="download-step">
              <h2>Step 2: Enter OTP</h2>
              <p className="otp-info">
                We've sent a 6-digit verification code to <strong>{email}</strong>.
                Please check your email and enter the code below.
              </p>
              {otpTimer > 0 && (
                <p className="otp-timer">Code expires in: {formatTime(otpTimer)}</p>
              )}
              <form onSubmit={handleOTPSubmit} className="download-form">
                <div className="form-group">
                  <label htmlFor="otp">Verification Code</label>
                  <input
                    type="text"
                    id="otp"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    maxLength="6"
                    required
                    disabled={loading}
                    className="otp-input"
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="download-btn" disabled={loading}>
                    {loading ? 'Verifying...' : 'Verify OTP'}
                  </button>
                  <button
                    type="button"
                    className="download-btn-secondary"
                    onClick={handleSendOTP}
                    disabled={loading || otpTimer > 540}
                  >
                    Resend OTP
                  </button>
                </div>
              </form>
            </div>
          )}

          {step === 3 && (
            <div className="download-step">
              <h2>Step 3: Download Files</h2>
              <p className="download-success">
                ✅ Email verified successfully! You can now download the files below.
              </p>
              <div className="download-files-list">
                {/* {abstractBooklet ? (
                  <div className="download-file-card">
                    <div className="file-info">
                      <h3>Abstract Booklet</h3>
                      <p className="file-name">{abstractBooklet.fileName}</p>
                    </div>
                    <button
                      className="download-file-btn"
                      onClick={() => handleDownload('abstract-booklet', abstractBooklet.fileName)}
                    >
                      Download
                    </button>
                  </div>
                ) : (
                  <div className="download-file-card disabled">
                    <div className="file-info">
                      <h3>Abstract Booklet</h3>
                      <p className="file-name">Not available yet</p>
                    </div>
                    <button className="download-file-btn" disabled>
                      Not Available
                    </button>
                  </div>
                )} */}

                {papersBook ? (
                  <div className="download-file-card">
                    <div className="file-info">
                      <h3>Conference Proceedings</h3>
                      <p className="file-name">{papersBook.fileName}</p>
                    </div>
                    <button
                      className="download-file-btn"
                      onClick={() => handleDownload('papers-book', papersBook.fileName)}
                    >
                      Download
                    </button>
                  </div>
                ) : (
                  <div className="download-file-card disabled">
                    <div className="file-info">
                      <h3>Papers Book</h3>
                      <p className="file-name">Not available yet</p>
                    </div>
                    <button className="download-file-btn" disabled>
                      Not Available
                    </button>
                  </div>
                )}
              </div>
              <button
                className="download-btn-secondary"
                onClick={() => {
                  setStep(1);
                  setEmail('');
                  setOtp('');
                  setVerifiedEmail('');
                }}
              >
                Start Over
              </button>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default DownloadFiles;

